<script lang="ts">
	// Stores
	import { devicesFilters } from 'src/stores/ui/filters';
	import { edit } from 'src/stores/ui/edit_lists';

	import { createListHeaderHandle, type ListHeaderConfig } from 'src/stores/list_header';
	// Design system components
	import { BreadcrumbsFlexWrapper, Toolbar } from '@Shelly/shelly-design-system';
	// Components
	import { createEventDispatcher, onDestroy } from 'svelte';
	import AddEditButtonsNew from '../../AddEditButtonsNew.svelte';
	import RoomToolbar from '../Rooms/RoomToolbar.svelte';
	import RoomBreadCrumbs from '../Rooms/RoomBreadCrumbs.svelte';
	import EditRoomWizard from './EditRoomWizard/EditRoomWizard.svelte';
	import RoomActionsAdapter from './RoomActionsAdapter.svelte';

	// Group this imports
	import { querystring, replace, location } from 'svelte-spa-router';
	import { is_mobile, is_tablet } from 'src/stores/ui/viewport';
	import DeleteRoomModal from './DeleteRoomModal/DeleteRoomModal.svelte';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { isActiveHomeShared } from 'src/helpers/houses/homes_helpers';
	import { homes } from 'src/stores/account/home';
	import { getSharedHomeManagedTooltip } from 'src/helpers/houses/shared_home_messaging_helpers';
	import SharedHomeOwnerManagedModal from 'src/components/SharedHome/SharedHomeOwnerManagedModal.svelte';

	export let id: string;
	export let list;
	export let showBulkActions: boolean = false;
	export let itemsLength: number = 0;
	export let showDeviceListToggle: boolean = false;

	const dispatch = createEventDispatcher<{ 'select-all': {}; 'submit-click': {} }>();
	const headerHandle = createListHeaderHandle();

	$: emptyList = itemsLength === 0;
	$: isSharedHomeActive = isActiveHomeShared($homes.list);

	let showEditRoomModal = false;
	let showDeleteRoomModal = false;
	let showSharedManagedModal = false;

	const routeFilter = Number($querystring.split('=')[1]);

	if (routeFilter) {
		Object.values($devicesFilters.appliance_types).map((filter) => {
			if (filter.id === routeFilter) {
				filter.selected = true;
			} else {
				filter.selected = false;
			}
			return filter;
		});
		replace(`${$location}`);
	}

	// -------- new-layout publisher --------
	// The room kebab (edit/delete room, reorder, bulk) already lives in
	// `AddEditButtonsNew`. Rather than reproducing that state machine as
	// data-driven menu items, we mount the whole component in `extraActions`.
	// In-room back/name + tabs render in `RoomHeaderChrome` (layout staticTop).
	$: if ($showNewLayout) {
		const config: ListHeaderConfig = {
			extraActions: [
				{
					key: 'room-actions',
					component: RoomActionsAdapter,
					props: {
						showBulkActions,
						showDeviceListToggle,
						disableEditButton: emptyList,
						disableReorderButton: itemsLength <= 1,
						managed: isSharedHomeActive,
						sharedHomeManagedTooltip: getSharedHomeManagedTooltip('change_view'),
						onEditRoomClick: () => {
							showEditRoomModal = true;
						},
						onDeleteRoomClick: () => {
							showDeleteRoomModal = true;
						},
						onManagedClick: () => (showSharedManagedModal = true),
						onAddClick: () => edit.set(false),
						onSelectAll: () => dispatch('select-all'),
						onSubmitClick: () => dispatch('submit-click'),
					},
				},
			],
		};
		headerHandle.set(config);
	}

	onDestroy(() => {
		headerHandle.clear();
	});
</script>

{#if !$showNewLayout}
	{#key showEditRoomModal}
		<Toolbar
			class="mb-m"
			hideSlotBottom={!$is_tablet || $is_mobile}
			hideSlotRightSide={$is_mobile}
			hideSlotLeftSide={!($is_mobile || (!$is_mobile && !$is_tablet))}
			isSticky
		>
			<BreadcrumbsFlexWrapper slot="breadcrumbs" gap="s" hideSlotRight={!$is_mobile}>
				<div slot="left" class="flex-1 min-width-0 max-width-fit-content">
					<RoomBreadCrumbs {id} />
				</div>

				<div slot="right" class="d-flex align-items-center gap-s">
					<AddEditButtonsNew
						{showBulkActions}
						managed={isSharedHomeActive}
						on:managed_click={() => (showSharedManagedModal = true)}
						on:add_click={async () => {
							edit.set(false);
						}}
						on:edit_room_click={() => {
							showEditRoomModal = true;
						}}
						on:delete_room_click={() => {
							showDeleteRoomModal = true;
						}}
						disableEditButton={emptyList}
						disableReorderButton={itemsLength <= 1}
						on:select-all
						on:submit-click
					/>
				</div>
			</BreadcrumbsFlexWrapper>

			<svelte:fragment slot="leftSide">
				<div class="d-flex gap-s max-width-fit-content align-items-center">
					<RoomToolbar />
				</div>
			</svelte:fragment>

			<svelte:fragment slot="rightSide">
				<AddEditButtonsNew
					{showBulkActions}
					managed={isSharedHomeActive}
					on:managed_click={() => (showSharedManagedModal = true)}
					on:add_click={async () => {
						edit.set(false);
					}}
					on:edit_room_click={() => {
						showEditRoomModal = true;
					}}
					on:delete_room_click={() => {
						showDeleteRoomModal = true;
					}}
					disableEditButton={emptyList}
					disableReorderButton={itemsLength <= 1}
					on:select-all
					on:submit-click
				/>
			</svelte:fragment>
		</Toolbar>
	{/key}
{/if}

<EditRoomWizard bind:showEditRoomModal {id} />
<DeleteRoomModal bind:showDeleteRoomModal {id} {list} />

<SharedHomeOwnerManagedModal bind:showModal={showSharedManagedModal} context="edit_entity" />
